import Service, { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';

import ApiService from 'cio/services/api';
import BackfillsService from 'cio/services/backfills';

export default class ExportsService extends Service {
  @service api!: ApiService;
  @service backfills!: BackfillsService;

  analysis({
    name,
    start,
    end,
    data_type,
    tag_ids,
    status,
    resolution,
    report_id,
  }: Record<string, string | number | (string | number)[]>) {
    let data = {
      name,
      start,
      end,
      // The Analysis page uses "transactional" instead of "transactional_message".
      // One of the reasons being that URL query params in the Analysis page use
      // underscored for separating lists of items, so we can't use names with
      // underscores in that page's params
      // eslint-disable-next-line @typescript-eslint/naming-convention
      data_type: (data_type as string[]).map((t) => (t === 'transactional' ? 'transactional_message' : t)),
      tag_ids,
      status,
      resolution,
      report_id,
    } as Record<string, string | number | (string | number)[]>;

    let request = this.api.post('exports/analysis', { data });

    request.then(() => this.backfills.fetch.perform(), ignorePromiseFailure);

    return request;
  }

  segmentMembershipMetrics({ id, start, end, resolution }: Record<string, string | number>) {
    let data = {
      segment_id: Number(id),
      start,
      end,
      resolution,
    } as Record<string, string | number>;

    let request = this.api.post('exports/segment_memberships', { data });

    request.then(() => this.backfills.fetch.perform(), ignorePromiseFailure);

    return request;
  }

  deliveryMetrics({ id, actionId, period, start, steps, from, deliveryType }: Record<string, string | number>) {
    let data = {
      period,
      campaign_id: id,
      exported_from: from,
      delivery_type: deliveryType,
    } as Record<string, string | number>;

    if (actionId) {
      data.action_id = actionId;
    }

    if (start && steps) {
      data.start = start;
      data.steps = steps;
    }

    let request = this.api.post('exports/delivery_metrics', { data });

    request.then(() => this.backfills.fetch.perform(), ignorePromiseFailure);

    return request;
  }

  deliveryTotals({ campaignId, newsletterId, templateId, from }: Record<string, string>) {
    if (isNone(campaignId) && isNone(newsletterId)) {
      throw 'You must provide either campaignIds, or newsletterId';
    }

    let data = {
      campaign_id: campaignId,
      newsletter_id: newsletterId,
      template_id: templateId,
      exported_from: from,
    };

    let request = this.api.post('exports/delivery_totals', { data });

    request.then(() => this.backfills.fetch.perform(), ignorePromiseFailure);

    return request;
  }

  newsletterDeliveryTotals({
    search,
    startTime,
    endTime,
    from,
    channel,
  }: {
    search?: string;
    startTime?: number;
    endTime?: number;
    from: string;
    channel: string;
  }) {
    let data = { search, start_time: startTime, end_time: endTime, exported_from: from, channel };

    let request = this.api.post('exports/newsletter_delivery_totals', { data });

    request.then(() => this.backfills.fetch.perform(), ignorePromiseFailure);

    return request;
  }

  journeyMetrics({ id, start, end, resolution }: { id: string; start: number; end: number; resolution: string }) {
    let data = { campaign_id: id, start, end, resolution };

    let request = this.api.post('exports/journey_metrics', { data });

    request.then(() => this.backfills.fetch.perform(), ignorePromiseFailure);

    return request;
  }

  trackedResponses({ id, start, end, resolution }: { id: string; start: number; end: number; resolution: string }) {
    let data = { campaign_id: id, start, end, resolution };

    let request = this.api.post('exports/tracked_responses', { data });

    request.then(() => this.backfills.fetch.perform(), ignorePromiseFailure);

    return request;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    exports: ExportsService;
  }
}
